<template src="./template.html"></template>
<style src="./styles.scss" lang="scss" scoped></style>

<script>
import {
  fixEditorContentForLineClamp,
  getSocialMediaIconForSource
} from '@/helper'
import { mapActions, mapState } from 'vuex'
import { isObjectLike, some } from 'lodash'
import api from '@/api'
import AttachmentItem from './AttachmentItem'
import LinkPreviewItem from './LinkPreviewItem'
import Gallery from './Gallery'
import EditPostOverlay from '@/components/overlays/EditPostOverlay'
import FallbackAvatar from '@/components/ui/FallbackAvatar'
import EventPreview from '@/components/layout/EventPreview'
import ConfirmationOverlay from '@/components/overlays/ConfirmationOverlay'
import OverlayGallery from '@/components/overlays/OverlayGallery'
import LumaCalculator from '@/components/helper/LumaCalculator'

export default {
  name: 'NewsItemOverlay',
  components: {
    AttachmentItem,
    LinkPreviewItem,
    EditPostOverlay,
    ConfirmationOverlay,
    CTA: () => import('@/components/modules/CTA'),
    OverlayGallery,
    EventPreview,
    FallbackAvatar,
    LumaCalculator,
    Gallery,
  },
  props: {
    news: { type: Object, default: null },
    showActionItems: Boolean,
    groupId: { type: Number, default: null },
    topicImg: String,
    topicId: { type: Number, default: null },
    hash: String,
    isOpen: Boolean,
    selectedArea: { type: String, default: null },
  },
  data() {
    return {
      overlayIndex: null,
      attachments: [],
      selectedOverlay: null,
      breadcrumbItems: [],
      isClamped: false,
      initialClamped: false,
      comments: {
        data: {},
      },
      likes: {
        data: {},
      },
      statusMessage: null,
      isStatusVisible: false,
      currentIndex: 0,
      luma: null,
      imageSource: null,
      indicators: 'bright',
    }
  },
  computed: {
    ...mapState('auth', ['settings']),
    isOpened: {
      set() {
        this.$emit('onCloseOverlay')
      },
      get() {
        return this.isOpen
      },
    },
    canDelete() {
      return this.news && some(this.news.permissions, p => p === 'delete')
    },
    canEdit() {
      return this.news && some(this.news.permissions, p => p === 'edit')
    },
    getDeletePostObj() {
      const delete_post_object = this.$t('news').news_item.overlays.delete_post
      delete_post_object['img'] = delete_post_object['img'] || this.topicImg
      return delete_post_object
    },
    showTitle() {
      const { title, teaser } = this.news
      return (
        title &&
        teaser.toLowerCase().indexOf(title.toLowerCase().slice(0, -4)) === -1
      )
    },
  },
  watch: {
    currentIndex: {
      immediate: true,
      handler(val) {
        this.onChangeSlide(val)
      },
    },
    luma: {
      immediate: true,
      handler(val) {
        this.indicators = val !== null && val > 155 ? 'dark' : 'bright'
      },
    },
  },
  created() {
    const meta = this.news.meta
    const breadcrumbItems = []
    if (meta.group) {
      breadcrumbItems.push({
        exact: true,
        text: meta.group.name,
        to: {
          name: 'group',
          params: { id: meta.group.id },
        },
        disabled: false,
      })
    }
    if (meta.topic) {
      if (meta.group) {
        breadcrumbItems.push({
          exact: true,
          text: meta.topic.name,
          to: {
            name: 'group',
            params: { id: meta.group.id },
            query: { topic_id: meta.topic.id },
          },
          disabled: false,
        })
      } else {
        breadcrumbItems.push({
          exact: true,
          text: meta.topic.name,
          to: {
            name: 'category',
            params: { id: meta.topic.id },
          },
          disabled: false,
        })
      }
    }
    this.breadcrumbItems = breadcrumbItems

    const news = this.news
    this.attachments =
      news.images && news.images.teaser
        ? [news.images.teaser]
        : news.attachments

    if (
      !Object.prototype.hasOwnProperty.call(news, 'likes_allowed') ||
      news.likes_allowed
    ) {
      this.fetchLikes().then(res => {
        if (this.selectedArea === 'likes') {
          const likeClick =
            this.$refs?.cta?.$refs?.likeButton?.$refs?.likeClick
          if (likeClick) {
            likeClick.click()
            likeClick.scrollIntoView()
          }
        }
      })
    }
    if (news.comments_allowed) {
      this.fetchComments().then(res => {
        if (this.selectedArea === 'comments') {
          window.refs = this.$refs
          const commentButton =
            this.$refs?.cta?.$refs?.commentButton?.$el
          if (commentButton) {
            commentButton.scrollIntoView()
          }
        }
      })
    }
    const moveForm = setInterval(() => {
      const commentForm =
        document.querySelector('.news-item-overlay .comment-form')
      const actions = document.querySelector('.news-item-overlay #news-item-overlay-actions')
      if (commentForm) {
        clearInterval(moveForm)
        actions.append(commentForm)
      }
    }, 200)
  },
  mounted() {
    this.onChangeSlide(0)
  },
  methods: {
    ...mapActions('posts', [
      'deletePost',
      'deleteComment',
    ]),
    onChangeSlide(index = 0) {
      const slide = this.attachments[index]
      if (!slide) {
        this.imageSource = null
        this.luma = 1
        return
      }
      const link = this.getLink(slide)
      const isImage = this.isImage(slide)
      if (isImage) {
        this.imageSource = new URL(link, document.baseURI).href
      } else {
        this.imageSource = null
        this.luma = 1
      }
    },
    isImage(attachment) {
      return (
        (isObjectLike(attachment) && attachment.type.indexOf('image') > -1) ||
        (!isObjectLike(attachment) &&
          attachment.match(/\.(jpeg|jpg|gif|png)$/) != null)
      )
    },
    getLink(attachment) {
      return isObjectLike(attachment) && attachment.link
        ? attachment.link
        : attachment
    },
    onLumaCalculated(value) {
      this.luma = value
    },
    closeOverlay() {
      this.selectedOverlay = null
    },
    async fetchComments() {
      api.items
        .getItems('comments', { type: 'news', id: this.news.id })
        .then(({ data }) => {
          this.comments.data = data
        })
    },
    async fetchLikes() {
      api.items
        .getItems('likes', { type: 'news', id: this.news.id })
        .then(({ data }) => {
          this.likes.data = data
        })
    },
    onLike(val) {
      api.items
        .editItem('likes', { type: 'news', id: this.news.id, like: val })
        .then(({ data }) => {
          this.likes.data = data
        })
    },
    onComment(val) {
      api.items
        .editItem('comments', { type: 'news', id: this.news.id, like: val })
        .then(({ data }) => {
          this.comments.data = data
          const oldComments =
            this.$refs?.cta?.$refs?.commentButton?.$refs?.oldComments
          if (oldComments) {
            oldComments.scrollIntoView()
          }
        })
    },
    fixEditorContentForLineClamp,
    getSocialMediaIconForSource,
    onDeletePost() {
      this.deletePost(this.news.id).then(_ => {
        this.statusMessage = this.$t(
          this.successStatusTranslation('post', 'delete'),
        )
        this.isStatusVisible = true
        this.closeOverlay()
      })
    },
    onClickAttachmentItem(index) {
      this.overlayIndex = index
    },
    onDeleteComment(selectedCommentId, postId) {
      this.deleteComment(selectedCommentId).then(_ => {
        api.items
          .getItems('comments', { type: 'news', id: postId })
          .then(({ data }) => {
            this.statusMessage = this.$t(
              this.successStatusTranslation('cta_comment', 'delete'),
            )
            this.isStatusVisible = true
            this.comments.data = data
          })
      })
    },
  },
}
</script>
